import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../utils/track';
import { fmtClock } from '../../utils/fmt';
import InfoTooltip from '../InfoTooltip';
import StatusDot from './StatusDot';
import './home.css';

/* ══════════════════════════════════════════════════════════════════════════
   KEZDŐLAP-MODUL — egységes fejléc: Cím ⓘ ●
   ─────────────────────────────────────────────────────────────────────────
   A forrás és a frissítés ideje nem látszik szövegként: az állapotpont
   tooltipje mutatja (ráhúzás / koppintás). Szöveg csak késésnél vagy hibánál
   jelenik meg. A kötelező attribúciók a láblécben vannak (DataCredits); ahol a
   szolgáltató az adat közelében kéri a nevét, ott `attr` (pl. <CoinGeckoMark />)
   ad egy apró, halvány jelölést a fejléc jobb oldalán.
   Állapot:
     ● zöld      — friss
     ● borostyán — „késleltetett”: az adat a várt frissítési idő kétszeresénél
                   régebbi; a számok halványítva
     ● szürke    — hiba („Bybit API nem elérhető”) + újrapróbálás; az utolsó jó
                   érték megmarad, a kártya nem ürül ki
   `at` a valódi lekérdezési idő (szerveroldali cache-nél a CoinGecko-hívás ideje).
   Mérés: home_module_viewed {module} — modulonként egyszer, amikor először
   látszik (IntersectionObserver); hiba esetén csak console.warn.
   ══════════════════════════════════════════════════════════════════════════ */

const seen = new Set();

function useNow(ms = 15000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

/** status: 'fresh' | 'delayed' | 'error' | 'loading' | 'static' */
export function moduleStatus({ at, expectedMs, error, stale, now = Date.now() }) {
  if (error || stale) return 'error';
  if (!expectedMs) return 'static';
  if (!at) return 'loading';
  return now - at > 2 * expectedMs ? 'delayed' : 'fresh';
}

export function trackHome(user, name, props) {
  try { trackEvent(name, props, user); } catch (e) { console.warn('[home]', name, e); }
}

export default function HomeModule({
  id, title, info, source, note, at, expectedMs, error, stale, errorText, onRetry,
  user, className = '', style, right, attr, children,
}) {
  const ref = useRef(null);
  const now = useNow();
  const status = moduleStatus({ at, expectedMs, error, stale, now });

  useEffect(() => {
    if (!id || seen.has(id) || !ref.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !seen.has(id)) {
        seen.add(id);
        trackHome(user, 'home_module_viewed', { module: id });
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [id, user]);

  const dotTitle = status === 'fresh' ? 'Friss adat'
    : status === 'delayed' ? 'Késleltetett: a vártnál régebbi adat'
    : status === 'error' ? 'Az adatforrás most nem elérhető' : status === 'loading' ? 'Betöltés…' : 'Kézzel karbantartott adat';
  const tipLines = [
    source && `Forrás: ${source}`,
    note,
    at && `Frissítve ${fmtClock(at)}`,
  ];

  return (
    <section ref={ref} className={`hm db-sec hm-${status} ${className}`} style={style} aria-label={typeof title === 'string' ? title : undefined} data-module={id}>
      <header className="hm-head">
        <span className="hm-title">
          {title}{info && <InfoTooltip text={info} />}
          <StatusDot status={status} label={dotTitle} lines={tipLines} />
          {status === 'delayed' && <span className="hm-late">késleltetett</span>}
        </span>
        {(attr || right) && <span className="hm-right">{attr}{right}</span>}
      </header>
      {status === 'error' && (
        <div className="hm-error" role="status">
          <span>{errorText || 'Az adatforrás most nem elérhető'}</span>
          {onRetry && <button type="button" className="hm-retry" onClick={onRetry}>újrapróbálás</button>}
        </div>
      )}
      <div className="hm-body">{children}</div>
    </section>
  );
}

/* CoinGecko: az attribution guide szerint az attribúció „close to where the
   data is displayed”; ez a legkisebb, halvány formája. A teljes
   „Powered by CoinGecko” (API Terms 4.4) a láblécben van. */
export const CG_URL = 'https://www.coingecko.com/?utm_source=web3kurzus&utm_medium=referral';
export function CoinGeckoMark() {
  return (
    <a className="hm-attr" href={CG_URL} target="_blank" rel="noopener noreferrer" title="Data provided by CoinGecko">
      CoinGecko
    </a>
  );
}
